import { useCallback } from 'react';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';
import { chatMessagesAtom, chatSessionIdAtom, chatStreamingAtom, chatTurnIdAtom } from 'src/shared/chat';
import { sendChatMessage, cancelChatTurn } from 'src/api/functions/chatApi';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useSSEStream } from './useSSEStream';
import { useChatContext } from './useChatContext';
import type { ChatMessage, PlanDecision } from './types';

async function getErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (response.status === 401) {
      return 'Session expired. Please log in again.';
    }
    if (response.status === 429) {
      const retryAfter = body.retry_after ? ` Try again in ${body.retry_after}s.` : '';
      return `Rate limited. Please wait.${retryAfter}`;
    }
    if (response.status === 503) {
      return 'Service temporarily unavailable. Please try again in a moment.';
    }
    return body.message || body.error || 'Something went wrong. Please try again.';
  } catch {
    return 'Something went wrong. Please try again.';
  }
}

export type SendOptions = {
  mcpPrompt?: string;
  mcpPromptArgs?: Record<string, string>;
  // Answer to a plan card (Approve / Cancel). The message is shown as the
  // user's bubble; the proxy acts on the decision.
  planDecision?: PlanDecision;
};

// The in-flight request's AbortController, shared by every useChatSend
// caller: only one chat turn streams at a time, and Stop in the input must
// also abort a turn started elsewhere (e.g. a plan card's Approve button).
let activeAbort: AbortController | null = null;

export type SendResult = { ok: true } | { ok: false; error: string };

/**
 * Send a chat message using the global chat session/messages atoms and the
 * SSE streaming pipeline. Extracted from ChatSidebar so other surfaces
 * (e.g. onboarding) can drive the same conversation without duplicating
 * the request/error/streaming logic.
 *
 * Returns `{ send, stop }`:
 *  - `send(message, opts)` posts the message and streams the response.
 *  - `stop()` cancels the in-flight turn: it tells the proxy to halt the
 *    agentic loop (which persists the partial output) and aborts the
 *    local fetch so the stream stops immediately. The partial assistant
 *    text already rendered is kept.
 */
export function useChatSend() {
  const setMessages = useUpdateAtom(chatMessagesAtom);
  const setStreaming = useUpdateAtom(chatStreamingAtom);
  const sessionId = useAtomValue(chatSessionIdAtom);
  const turnId = useAtomValue(chatTurnIdAtom);
  const accessToken = useAccessToken();
  const chatContext = useChatContext();
  const { processStream } = useSSEStream();

  const send = useCallback(
    async (message: string, opts?: SendOptions): Promise<SendResult> => {
      const userMsg: ChatMessage = {
        id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: 'user',
        content: message,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);
      // Streaming starts now, not when the response arrives: the proxy takes
      // a moment to answer, and MessageList shows the working indicator under
      // the user's message until the assistant message exists.
      setStreaming(true);

      const controller = new AbortController();
      activeAbort = controller;

      try {
        const response = await sendChatMessage({
          message,
          context: chatContext,
          sessionId: sessionId || undefined,
          accessToken,
          mcpPrompt: opts?.mcpPrompt,
          mcpPromptArgs: opts?.mcpPromptArgs,
          planDecision: opts?.planDecision,
          signal: controller.signal,
        });

        if (!response.ok) {
          const errText = await getErrorMessage(response);
          const errorMsg: ChatMessage = {
            id: `msg-${Date.now()}-err`,
            role: 'assistant',
            content: errText,
            timestamp: Date.now(),
          };
          setMessages((prev) => [...prev, errorMsg]);
          return { ok: false as const, error: errText };
        }

        await processStream(response);
        return { ok: true as const };
      } catch (err: any) {
        // An intentional Stop aborts the fetch. Don't render a
        // "Failed to connect" bubble or replace the partial assistant
        // message — useSSEStream already swallows the read abort and
        // keeps the streamed text.
        if (err?.name === 'AbortError') {
          return { ok: true as const };
        }
        const errText = 'Failed to connect. Please try again.';
        const errorMsg: ChatMessage = {
          id: `msg-${Date.now()}-err`,
          role: 'assistant',
          content: errText,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, errorMsg]);
        return { ok: false as const, error: errText };
      } finally {
        if (activeAbort === controller) activeAbort = null;
        // processStream clears it too; this covers the error paths that
        // never reach it.
        setStreaming(false);
      }
    },
    [chatContext, sessionId, setMessages, setStreaming, processStream, accessToken],
  );

  const stop = useCallback(() => {
    // Tell the proxy to cancel the running loop first (so it can persist
    // partial output and emit message_cancelled), then abort the local
    // fetch to stop reading the stream. Order is best-effort: the abort
    // alone also cancels the request context server-side.
    if (turnId) {
      void cancelChatTurn(turnId, accessToken);
    }
    activeAbort?.abort();
    activeAbort = null;
  }, [turnId, accessToken]);

  return { send, stop };
}
